import type { MonthlySeriesPoint, PnlResponse } from '@/types/schemas';
import { periodShortLabel, safeDivide, toNumber } from './format';
import type { MonthPoint } from '@/components/charts';

/** One metric's values across the KPI endpoint's `monthly_series`, in month
 * order. Every number here was already computed by the backend — this only
 * picks a column out, it never derives a new figure. */
export function monthlySeriesValues(points: MonthlySeriesPoint[], key: string): (number | null)[] {
  return points.map((p) => toNumber(p.values[key] ?? null));
}

export function monthlySeriesLabels(points: MonthlySeriesPoint[]): string[] {
  return points.map((p) => periodShortLabel(p.month));
}

/** Values of one category code across the year's periods, in month order. */
export function seriesOf(pnl: PnlResponse, code: string): (number | null)[] {
  const row = pnl.rows.find((r) => r.code === code);
  return pnl.periods.map((p) => {
    const key = `${p.year}-${String(p.month).padStart(2, '0')}`;
    return row ? toNumber(row.values[key]) : null;
  });
}

export function monthLabels(pnl: PnlResponse): string[] {
  return pnl.periods.map((p) => periodShortLabel(p.month));
}

/** Trailing 3-month moving average; null while fewer than 2 points exist. */
export function movingAverage3(series: (number | null)[]): (number | null)[] {
  return series.map((_, index) => {
    const window = series.slice(Math.max(0, index - 2), index + 1).filter((x): x is number => x != null);
    if (window.length === 0) return null;
    return window.reduce((a, b) => a + b, 0) / window.length;
  });
}

export function buildPoints(
  labels: string[],
  seriesByName: Record<string, (number | null)[]>,
): MonthPoint[] {
  return labels.map((month, index) => {
    const point: MonthPoint = { month };
    for (const [name, series] of Object.entries(seriesByName)) point[name] = series[index];
    return point;
  });
}

/** Presentational ratio series (e.g. margin) from two money series. */
export function ratioSeries(numerator: (number | null)[], denominator: (number | null)[]): (number | null)[] {
  return numerator.map((value, index) => safeDivide(value, denominator[index]));
}
